import { useCallback, useEffect, useRef, useState } from 'react';

const mediaState = (audio, confirmedPlaying = false, starting = false) => ({
    source: audio?.src,
    playing: confirmedPlaying && !audio?.paused && !audio?.ended,
    preparing: starting || Boolean(audio && !audio.paused && !audio.ended && !audio.error &&
        (!confirmedPlaying || audio.readyState < HTMLMediaElement.HAVE_FUTURE_DATA)),
    failed: Boolean(audio?.error),
    ended: Boolean(audio?.ended) || Boolean(Number.isFinite(audio?.duration) && audio.duration > 0 &&
        Number.isFinite(audio.currentTime) && audio.currentTime >= audio.duration && !audio.seeking && !starting),
});

export default function usePreviewPlaybackControl({
    audio, source, active, viewerAvailable, willAutomaticallyAdvance, finished = false,
    showsPlaybackHint = true, onPlaybackHintExpired, autoplayPending = false, keepVisible = false,
}) {
    const confirmed = useRef(false);
    const starting = useRef(false);
    const playAttempt = useRef(0);
    const hasPlayedSource = useRef(false);
    const playRejected = useRef(false);
    const hintGeneration = useRef(0);
    const [resumeHint, setResumeHint] = useState(0);
    const [fadeOnHide, setFadeOnHide] = useState(false);
    const [media, setMedia] = useState(() => mediaState(audio));
    const [pageVisible, setPageVisible] = useState(() => !document.hidden);
    const update = useCallback((skipUnchanged = false) => {
        const next = mediaState(audio, confirmed.current, starting.current);
        setMedia(previous => skipUnchanged && Object.keys(next).every(key => previous[key] === next[key])
            ? previous : next);
    }, [audio]);
    const revealControl = useCallback(() => {
        setFadeOnHide(false);
        setResumeHint(++hintGeneration.current);
    }, []);

    useEffect(() => {
        if (!audio) return undefined;
        // Hints belong to this source, including when a card is reused in place.
        hintGeneration.current += 1;
        setResumeHint(0);
        setFadeOnHide(false);
        confirmed.current = audio.src === source && !audio.paused && !audio.ended &&
            audio.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA;
        hasPlayedSource.current = confirmed.current;
        playRejected.current = false;
        starting.current = false;
        update();
        const listener = event => {
            const currentSource = audio.src === source;
            if (!currentSource) {
                hasPlayedSource.current = false;
                playRejected.current = false;
                setResumeHint(0);
            }
            // Only timer expiry fades a control. A changed playback phase hides
            // it immediately, without briefly revealing the new glyph.
            if (event.type !== 'timeupdate') setFadeOnHide(false);
            if (!currentSource || event.type === 'ended' || event.type === 'error' ||
                (event.type === 'pause' && audio.paused)) {
                confirmed.current = false;
                starting.current = false;
                playAttempt.current += 1;
            } else if (event.type === 'playing') {
                confirmed.current = true;
                hasPlayedSource.current = true;
                starting.current = false;
            } else if (['emptied', 'loadstart', 'play', 'waiting', 'stalled'].includes(event.type)) {
                // Queued loadstart/emptied events must not cancel their
                // same-source play request before its rejection is handled.
                confirmed.current = false;
            }
            // Progress events reach every mounted card. Only the end boundary
            // changes this control's state; ordinary progress needs no render.
            update(event.type === 'timeupdate');
        };
        const events = ['play', 'playing', 'pause', 'waiting', 'stalled', 'loadstart', 'emptied',
            'loadedmetadata', 'loadeddata', 'canplay', 'seeking', 'seeked', 'timeupdate', 'ended', 'error'];
        events.forEach(event => audio.addEventListener(event, listener));
        return () => {
            playAttempt.current += 1;
            events.forEach(event => audio.removeEventListener(event, listener));
        };
    }, [audio, source, update]);

    useEffect(() => {
        if (!active) {
            hasPlayedSource.current = false;
            setResumeHint(0);
            setFadeOnHide(false);
        }
    }, [active]);

    useEffect(() => {
        const visibilityChanged = () => setPageVisible(!document.hidden);
        document.addEventListener('visibilitychange', visibilityChanged);
        return () => document.removeEventListener('visibilitychange', visibilityChanged);
    }, []);

    const requestPlayback = useCallback(({ reveal = true, start } = {}) => {
        if (reveal) revealControl();
        else setFadeOnHide(false);
        const attempt = ++playAttempt.current;
        const requestedSource = source;
        playRejected.current = false;
        starting.current = true;
        update();
        let request;
        try {
            request = start ? start() : audio.play();
        } catch (error) {
            request = Promise.reject(error);
        }
        return Promise.resolve(request).then(() => {
            if (attempt !== playAttempt.current || audio.src !== requestedSource) return;
            starting.current = false;
            confirmed.current = !audio.paused && !audio.ended;
            hasPlayedSource.current = confirmed.current;
            update();
        }, error => {
            if (attempt === playAttempt.current && audio.src === requestedSource) {
                starting.current = false;
                confirmed.current = false;
                playRejected.current = true;
                update();
            }
            throw error;
        });
    }, [audio, source, update, revealControl]);

    const matchesSource = media.source === source && audio?.src === source;
    // A native pause can arrive before ended and before the queue changes.
    const actualEnd = mediaState(audio, confirmed.current, starting.current).ended;
    const available = active && (!matchesSource ||
        media.failed || !willAutomaticallyAdvance || (!finished && !actualEnd));
    // A newly selected source may still be paused while autoplay is being
    // scheduled. It is not yet a user-paused preview with a Play action.
    const awaitingAutoplay = autoplayPending && !hasPlayedSource.current && !playRejected.current;
    const preparing = matchesSource && (media.preparing || awaitingAutoplay) && !media.failed && !finished;
    const playing = matchesSource && media.playing;
    const hintVisible = showsPlaybackHint || resumeHint !== 0;
    const canFade = hintVisible && available && viewerAvailable && pageVisible &&
        playing && !preparing && !media.failed;
    useEffect(() => {
        if (!canFade) return undefined;
        const generation = hintGeneration.current;
        const timer = setTimeout(() => {
            // An explicit request can renew the hint before effect cleanup.
            if (generation !== hintGeneration.current) return;
            setFadeOnHide(true);
            setResumeHint(0);
            if (showsPlaybackHint) onPlaybackHintExpired?.();
        }, 3500);
        return () => clearTimeout(timer);
    }, [canFade, resumeHint, showsPlaybackHint, onPlaybackHintExpired]);

    return {
        requestPlayback, revealControl, available, preparing, playing,
        showControl: available && (keepVisible || hintVisible || preparing || !playing || media.failed || finished),
        fadeControl: !keepVisible && fadeOnHide && !hintVisible && playing && !preparing,
    };
}
